import { inject } from '@angular/core';
import { getSelfRegistration, getTariffReleaseInMinutes } from '@app/app.reducer';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { BookingProductType, VoucherBookingCountdown } from '@products/models/booking.model';
import { BookingCountdownService } from '@products/services/booking-countdown.service';
import { PackageBookingService } from '@products/services/package-booking.service';
import { BookingCountdownActions } from '@store/products/booking/countdown/booking-countdown.actions';
import { PackageProductBookingActions } from '@store/products/booking/product/package/package-booking.actions';
import { ProductBookingActions } from '@store/products/booking/product/product-booking.actions';
import { getAllBookedTariffs } from '@store/products/booking/product/product-booking.selectors';
import { VoucherProductBookingActions } from '@store/products/booking/product/voucher/voucher-booking.actions';
import { ProductSelectionActions } from '@store/products/product-selection/product-selection.actions';
import { isEmpty, isNil } from 'lodash';
import { EMPTY, filter, first, map, of, switchMap } from 'rxjs';

export const setProductBookingCountdown$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), packageBookingService = inject(PackageBookingService)) =>
    actions$.pipe(
      ofType(
        ProductBookingActions.setProductInBookingList,
        ProductBookingActions.removeBookedProductsAndSetPreferredProductInBookingList,
        PackageProductBookingActions.setPackageProductInBookingList
      ),
      concatLatestFrom(() => [store.select(getTariffReleaseInMinutes), store.select(getSelfRegistration)]),
      filter(([_, tariffReleaseInMinutes, isSelfRegistration]) => !!tariffReleaseInMinutes && !isSelfRegistration),
      switchMap(([{ bookingProductTypes, type }, tariffReleaseInMinutes]) => {
        if (
          isEmpty(bookingProductTypes) ||
          (type === PackageProductBookingActions.setPackageProductInBookingList.type &&
            !packageBookingService.isPackageProductAddedInProductBookingList(bookingProductTypes))
        ) {
          return EMPTY;
        }

        return of(BookingCountdownActions.setProductBookingCountdown({ releaseInMinutes: tariffReleaseInMinutes }));
      })
    ),
  { functional: true }
);

export const removeProductBookingCountdown$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) =>
    actions$.pipe(
      ofType(
        ProductBookingActions.removeProductFromBookingList,
        VoucherProductBookingActions.removeVoucherProductFromBookingList,
        PackageProductBookingActions.removePackageFromBookingList
      ),
      concatLatestFrom(() => [
        store.select(getAllBookedTariffs),
        store.select(getTariffReleaseInMinutes),
        store.select(getSelfRegistration)
      ]),
      filter(
        ([_, __, tariffReleaseInMinutes, isSelfRegistration]) => !isNil(tariffReleaseInMinutes) && !isSelfRegistration
      ),
      map(([_, allBookedTariffs, tariffReleaseInMinutes]) =>
        BookingCountdownActions.setProductBookingCountdown({
          releaseInMinutes: !isEmpty(allBookedTariffs) ? tariffReleaseInMinutes : null
        })
      )
    ),
  { functional: true }
);

export const setVoucherBookingCountdown$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), bookingCountdownService = inject(BookingCountdownService)) =>
    actions$.pipe(
      ofType(ProductSelectionActions.setVoucherProductInSelectionList),
      concatLatestFrom(() =>
        store.pipe(
          select(getTariffReleaseInMinutes),
          first((tariffReleaseInMinutes) => !!tariffReleaseInMinutes)
        )
      ),
      map(([{ bookingProductTypes }, tariffReleaseInMinutes]) => {
        const voucherBookingCountdowns: VoucherBookingCountdown[] =
          bookingCountdownService.mapBookingProductTypesToVoucherBookingCountdowns(
            bookingProductTypes,
            tariffReleaseInMinutes
          );

        return BookingCountdownActions.setVoucherProductBookingCountdown(voucherBookingCountdowns);
      })
    ),
  { functional: true }
);

export const removeVoucherBookingCountdown$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), bookingCountdownService = inject(BookingCountdownService)) =>
    actions$.pipe(
      ofType(
        ProductSelectionActions.removeVoucherProductFromSelectionList,
        ProductSelectionActions.removeVoucherCodeProductFromSelectionList
      ),
      concatLatestFrom(() =>
        store.pipe(
          select(getTariffReleaseInMinutes),
          first((tariffReleaseInMinutes) => !!tariffReleaseInMinutes)
        )
      ),
      map(([{ type, payload }, tariffReleaseInMinutes]) => {
        let voucherBookingCountdowns: VoucherBookingCountdown[] = [];

        if (type === ProductSelectionActions.removeVoucherProductFromSelectionList.type) {
          const bookingProductTypes = payload as BookingProductType[];
          voucherBookingCountdowns = bookingCountdownService.mapBookingProductTypesToVoucherBookingCountdowns(
            bookingProductTypes,
            tariffReleaseInMinutes
          );
        } else {
          const voucherCode = payload as string;
          voucherBookingCountdowns = [{ voucherCode, expiryTimestamp: 0 }];
        }

        return BookingCountdownActions.removeVoucherProductBookingCountdown(
          voucherBookingCountdowns,
          tariffReleaseInMinutes
        );
      })
    ),
  { functional: true }
);
